import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';

const sections = [
  { id: 'verantwortlicher', title: '1. Verantwortlicher' },
  { id: 'hosting', title: '2. Hosting & CDN (Cloudflare)' },
  { id: 'server-logfiles', title: '3. Server-Logfiles' },
  { id: 'kontaktformular', title: '4. Kontaktformular (Formspree)' },
  { id: 'cookies', title: '5. Cookies & Tracking' },
  { id: 'rechtsgrundlagen', title: '6. Rechtsgrundlagen (Art. 6 DSGVO)' },
  { id: 'speicherdauer', title: '7. Speicherdauer' },
  { id: 'betroffenenrechte', title: '8. Ihre Rechte (Art. 15–21 DSGVO)' },
  { id: 'aufsichtsbehoerde', title: '9. Beschwerderecht – Aufsichtsbehörde' },
  { id: 'drittlaender', title: '10. Drittlandübermittlung' },
  { id: 'aktualitaet', title: '11. Aktualität dieser Erklärung' },
];

export default function DatenschutzPage() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kustura-sportscar-insurance.de';

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEOHead
        title="Datenschutzerklärung – Kustura Sportscar Insurance"
        description="Datenschutzerklärung gemäß DSGVO für Kustura Sportscar Insurance, Vedran Kustura, Generalvertretung der Allianz, Essen."
        keywords="Datenschutz, DSGVO, Datenschutzerklärung, Kustura, Sportscar Insurance"
        canonical={`${siteUrl}/datenschutz`}
      />

      {/* ── Nav bar ─────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 bg-black/95 backdrop-blur-md z-50 border-b border-[#C9A961]/20">
        <div className="w-full px-4 md:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#C9A961] hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap group"
          >
            <i className="ri-arrow-left-line text-lg group-hover:-translate-x-1 transition-transform duration-200"></i>
            <span style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '16px', letterSpacing: '0.15em' }}>
              Zurück zur Startseite
            </span>
          </button>
          <span
            className="text-white text-sm tracking-widest hidden md:block"
            style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '18px', letterSpacing: '0.1em' }}
          >
            Datenschutzerklärung
          </span>
          <a
            href="tel:020152091641"
            className="flex items-center space-x-2 text-[#C9A961] hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-base"></i>
            <span className="hidden lg:inline text-sm" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '16px' }}>
              0201 52091641
            </span>
          </a>
        </div>
      </nav>

      {/* ── Main ────────────────────────────────────────── */}
      <main className="bg-black min-h-screen pt-20 pb-20" style={{ fontFamily: 'Inter, sans-serif' }}>
        {/* Hero header */}
        <div className="relative py-16 px-6 overflow-hidden border-b border-[#C9A961]/20">
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'radial-gradient(#C9A961 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
          <div className="relative z-10 max-w-4xl mx-auto">
            <p className="text-[#C9A961] text-xs tracking-[0.3em] uppercase mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '13px' }}>
              Rechtliche Informationen
            </p>
            <h1 className="text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 'clamp(2.4rem, 6vw, 4rem)', letterSpacing: '0.05em', lineHeight: 1.05 }}>
              Datenschutzerklärung
            </h1>
            <p className="text-gray-400 text-sm max-w-xl leading-relaxed">
              Gemäß Datenschutz-Grundverordnung (DSGVO) und Bundesdatenschutzgesetz (BDSG).
              Stand: {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 pt-12">
          {/* Table of Contents */}
          <nav className="bg-zinc-950 border border-[#C9A961]/25 rounded-lg p-6 mb-12" aria-label="Inhaltsverzeichnis">
            <h2 className="text-[#C9A961] text-sm font-semibold tracking-widest uppercase mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '15px' }}>
              Inhaltsverzeichnis
            </h2>
            <ul className="space-y-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => scrollTo(s.id)}
                    className="text-gray-400 hover:text-[#C9A961] transition-colors duration-150 text-sm text-left cursor-pointer leading-relaxed"
                  >
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* ─── 1. Verantwortlicher ─────────────────────── */}
          <section id="verantwortlicher" className="mb-12 scroll-mt-24">
            <SectionTitle>1. Verantwortlicher</SectionTitle>
            <Prose>
              <p>
                Verantwortlicher im Sinne der DSGVO für die Verarbeitung personenbezogener Daten auf dieser Website ist:
              </p>
              <AddressBlock>
                <strong className="text-white">Vedran Kustura</strong><br />
                Generalvertretung der Allianz<br />
                Alfredstraße 102<br />
                45131 Essen<br /><br />
                <LinkInline href="tel:020152091641">Telefon: 0201 52091641</LinkInline><br />
                <LinkInline href="mailto:team@kustura-sportscar-insurance.de">E-Mail: team@kustura-sportscar-insurance.de</LinkInline>
              </AddressBlock>
            </Prose>
          </section>

          {/* ─── 2. Hosting & CDN ────────────────────────── */}
          <section id="hosting" className="mb-12 scroll-mt-24">
            <SectionTitle>2. Hosting &amp; CDN – Cloudflare Pages</SectionTitle>
            <Prose>
              <p>
                Diese Website wird über <strong className="text-white">Cloudflare Pages</strong> gehostet. Zudem nutzen wir die Content Delivery Network (CDN)-Infrastruktur sowie den DDoS-Schutz von Cloudflare.
              </p>
              <InfoBox>
                <BoldLabel>Anbieter:</BoldLabel> Cloudflare, Inc., 101 Townsend St., San Francisco, CA 94107, USA<br />
                <BoldLabel>Zweck:</BoldLabel> Bereitstellung, Auslieferung und Absicherung der Website; Schutz vor Angriffen (DDoS); weltweite Verfügbarkeit über Edge-Server<br />
                <BoldLabel>Verarbeitete Daten:</BoldLabel> IP-Adressen aller Website-Besucher, HTTP-Anfragen, Geräteinformationen, Zeitstempel, aufgerufene Seiten, Referrer-URL, verwendeter Browser<br />
                <BoldLabel>Rechtsgrundlage:</BoldLabel> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einem sicheren, schnellen und stabilen Betrieb der Website)<br />
                <BoldLabel>Drittlandübermittlung:</BoldLabel> Cloudflare ist zertifiziert nach dem <em>EU-U.S. Data Privacy Framework</em> (DPF); zusätzlich gelten Standarddatenschutzklauseln (SCCs) gemäß Art. 46 Abs. 2 lit. c DSGVO.<br />
                <BoldLabel>Datenschutzrichtlinie Cloudflare:</BoldLabel>{' '}
                <LinkInline href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer">
                  https://www.cloudflare.com/privacypolicy/
                </LinkInline>
              </InfoBox>
            </Prose>
          </section>

          {/* ─── 3. Server-Logfiles ──────────────────────── */}
          <section id="server-logfiles" className="mb-12 scroll-mt-24">
            <SectionTitle>3. Server-Logfiles</SectionTitle>
            <Prose>
              <p>
                Beim Aufruf dieser Website werden durch Cloudflare automatisch sogenannte Server-Logfiles erhoben und gespeichert. Diese enthalten:
              </p>
              <ul className="list-disc list-inside space-y-1 pl-2 mt-3">
                <li>IP-Adresse des anfragenden Geräts</li>
                <li>Datum und Uhrzeit des Zugriffs (Zeitstempel)</li>
                <li>Aufgerufene URL und HTTP-Statuscode</li>
                <li>Verwendeter Browser (User-Agent-String) und Betriebssystem</li>
                <li>Referrer-URL (zuvor besuchte Seite)</li>
                <li>Übertragene Datenmenge</li>
              </ul>
              <p className="mt-4">
                <BoldLabel>Zweck:</BoldLabel> Gewährleistung des stabilen und sicheren Betriebs der Website, Erkennung und Abwehr von Angriffen sowie Fehleranalyse.<br />
                <BoldLabel>Rechtsgrundlage:</BoldLabel> Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an IT-Sicherheit und Betrieb).<br />
                <BoldLabel>Speicherdauer:</BoldLabel> Logfiles werden von Cloudflare in der Regel für maximal 30 Tage gespeichert, soweit sie nicht für die Abwehr eines laufenden Angriffs länger benötigt werden.
              </p>
              <p className="mt-3">
                Eine Zusammenführung dieser Daten mit anderen Datenquellen findet nicht statt. Die Erhebung dieser Daten ist technisch zwingend erforderlich, um die Website ausliefern zu können.
              </p>
            </Prose>
          </section>

          {/* ─── 4. Kontaktformular ──────────────────────── */}
          <section id="kontaktformular" className="mb-12 scroll-mt-24">
            <SectionTitle>4. Kontaktformular – Formspree</SectionTitle>
            <Prose>
              <p>
                Auf dieser Website steht ein Kontaktformular zur Verfügung. Wenn Sie das Formular nutzen, werden die von Ihnen eingegebenen Daten an den Dienst <strong className="text-white">Formspree</strong> übermittelt und von dort an uns weitergeleitet.
              </p>
              <InfoBox>
                <BoldLabel>Anbieter:</BoldLabel> Formspree, Inc., USA<br />
                <BoldLabel>Zweck:</BoldLabel> Entgegennahme und Weiterleitung von Kontaktanfragen<br />
                <BoldLabel>Verarbeitete Daten:</BoldLabel> Name, E-Mail-Adresse, Telefonnummer (falls angegeben), Fahrzeugdaten (falls angegeben), Nachrichteninhalt, IP-Adresse, Zeitstempel<br />
                <BoldLabel>Rechtsgrundlage:</BoldLabel> Art. 6 Abs. 1 lit. b DSGVO (Anbahnung / Durchführung eines Vertragsverhältnisses bzw. vorvertragliche Maßnahmen); hilfsweise Art. 6 Abs. 1 lit. f DSGVO<br />
                <BoldLabel>Drittlandübermittlung:</BoldLabel> Schutz durch EU-Standarddatenschutzklauseln (SCCs) gemäß Art. 46 Abs. 2 lit. c DSGVO<br />
                <BoldLabel>Datenschutzrichtlinie Formspree:</BoldLabel>{' '}
                <LinkInline href="https://formspree.io/legal/privacy-policy/" target="_blank" rel="noopener noreferrer">
                  https://formspree.io/legal/privacy-policy/
                </LinkInline>
              </InfoBox>
              <p>
                Die übermittelten Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet. Wir geben Ihre Daten nicht an Dritte weiter, sofern dies nicht zur Bearbeitung Ihres Anliegens erforderlich ist oder Sie ausdrücklich eingewilligt haben.
              </p>
            </Prose>
          </section>

          {/* ─── 5. Cookies ──────────────────────────────── */}
          <section id="cookies" className="mb-12 scroll-mt-24">
            <SectionTitle>5. Cookies &amp; Tracking</SectionTitle>
            <Prose>
              <p>
                Auf dieser Website werden <strong className="text-white">keine Cookies zu Tracking- oder Werbezwecken</strong> eingesetzt. Es werden keine Analyse-Tools (z. B. Google Analytics), Werbenetzwerke oder Social-Media-Tracking-Pixel verwendet.
              </p>
              <p>
                Cloudflare kann für technische Zwecke (z. B. DDoS-Schutz, Bot-Erkennung) sogenannte technisch notwendige Cookies oder Local-Storage-Einträge setzen. Diese dienen ausschließlich der technischen Bereitstellung und Sicherheit der Website und erfordern keine Einwilligung nach Art. 6 Abs. 1 lit. f DSGVO.
              </p>
              <p>
                Ein Cookie-Banner ist daher auf dieser Website bewusst nicht erforderlich.
              </p>
            </Prose>
          </section>

          {/* ─── 6. Rechtsgrundlagen ─────────────────────── */}
          <section id="rechtsgrundlagen" className="mb-12 scroll-mt-24">
            <SectionTitle>6. Rechtsgrundlagen der Verarbeitung (Art. 6 DSGVO)</SectionTitle>
            <Prose>
              <p>Alle Verarbeitungsvorgänge auf dieser Website stützen sich auf die folgenden Rechtsgrundlagen:</p>
              <div className="mt-4 space-y-3">
                <RechtsRow art="Art. 6 Abs. 1 lit. b DSGVO">
                  Kontaktformular: Verarbeitung zur Anbahnung oder Erfüllung eines Vertrags bzw. zur Durchführung vorvertraglicher Maßnahmen auf Anfrage der betroffenen Person.
                </RechtsRow>
                <RechtsRow art="Art. 6 Abs. 1 lit. f DSGVO">
                  Server-Logfiles, Hosting über Cloudflare, CDN, DDoS-Schutz: Berechtigtes Interesse an einem sicheren, stabilen und schnellen Betrieb der Website sowie dem Schutz vor Cyberangriffen.
                </RechtsRow>
              </div>
              <p className="mt-4">
                Es werden keine Daten auf Grundlage einer Einwilligung (lit. a) oder rechtlicher Verpflichtung (lit. c) verarbeitet, soweit nicht ausdrücklich anders angegeben.
              </p>
            </Prose>
          </section>

          {/* ─── 7. Speicherdauer ────────────────────────── */}
          <section id="speicherdauer" className="mb-12 scroll-mt-24">
            <SectionTitle>7. Speicherdauer</SectionTitle>
            <Prose>
              <div className="space-y-3 mt-2">
                <DauerRow label="Server-Logfiles (Cloudflare)">
                  In der Regel bis zu 30 Tage; bei Sicherheitsvorfällen bis zur abschließenden Klärung, maximal entsprechend den gesetzlichen Aufbewahrungsfristen.
                </DauerRow>
                <DauerRow label="Kontaktformular-Daten (Formspree)">
                  Bis zur abschließenden Bearbeitung Ihrer Anfrage; anschließend bei uns nach den handels- und steuerrechtlichen Aufbewahrungsfristen (in der Regel 6–10 Jahre), sofern ein Vertragsverhältnis begründet wurde; andernfalls nach Abschluss der Kommunikation.
                </DauerRow>
                <DauerRow label="Kontaktaufnahmen per E-Mail / Telefon">
                  Bis zur Erledigung des Anliegens; bei Geschäftsvorgängen entsprechend der gesetzlichen Aufbewahrungspflichten (§ 257 HGB, § 147 AO).
                </DauerRow>
              </div>
              <p className="mt-4">
                Nach Ablauf der jeweils anwendbaren Fristen werden die Daten routinemäßig gelöscht oder gesperrt, sofern keine gesetzliche Aufbewahrungspflicht entgegensteht.
              </p>
            </Prose>
          </section>

          {/* ─── 8. Betroffenenrechte ────────────────────── */}
          <section id="betroffenenrechte" className="mb-12 scroll-mt-24">
            <SectionTitle>8. Ihre Rechte als betroffene Person (Art. 15–21 DSGVO)</SectionTitle>
            <Prose>
              <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
              <div className="mt-5 space-y-4">
                <RechtBlock art="Recht auf Auskunft (Art. 15 DSGVO)">
                  Sie können jederzeit Auskunft über die von uns verarbeiteten personenbezogenen Daten, deren Herkunft, Empfänger und den Zweck der Verarbeitung verlangen.
                </RechtBlock>
                <RechtBlock art="Recht auf Berichtigung (Art. 16 DSGVO)">
                  Sie haben das Recht, unrichtige oder unvollständige Daten unverzüglich berichtigen zu lassen.
                </RechtBlock>
                <RechtBlock art="Recht auf Löschung (Art. 17 DSGVO)">
                  Sie können die Löschung Ihrer personenbezogenen Daten verlangen, sofern die gesetzlichen Voraussetzungen erfüllt sind und keine Aufbewahrungspflichten entgegenstehen.
                </RechtBlock>
                <RechtBlock art="Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)">
                  Sie können die Einschränkung der Verarbeitung Ihrer Daten verlangen, z. B. wenn Sie die Richtigkeit der Daten bestreiten oder der Verarbeitung widersprochen haben.
                </RechtBlock>
                <RechtBlock art="Recht auf Datenübertragbarkeit (Art. 20 DSGVO)">
                  Sie haben das Recht, die Sie betreffenden Daten in einem strukturierten, gängigen und maschinenlesbaren Format zu erhalten oder deren Übertragung an einen anderen Verantwortlichen zu verlangen, soweit die Verarbeitung auf einer Einwilligung oder einem Vertrag beruht.
                </RechtBlock>
                <RechtBlock art="Widerspruchsrecht (Art. 21 DSGVO)">
                  Sie haben das Recht, der Verarbeitung Ihrer Daten, die auf Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse) beruht, jederzeit mit Wirkung für die Zukunft zu widersprechen. Wir verarbeiten Ihre Daten dann nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und Freiheiten überwiegen.
                </RechtBlock>
                <RechtBlock art="Recht auf Widerruf einer Einwilligung">
                  Soweit die Verarbeitung auf einer Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO beruht, können Sie diese jederzeit mit Wirkung für die Zukunft widerrufen. Die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt unberührt.
                </RechtBlock>
              </div>
              <p className="mt-6">
                Zur Ausübung Ihrer Rechte wenden Sie sich bitte an:<br />
                <LinkInline href="mailto:team@kustura-sportscar-insurance.de">team@kustura-sportscar-insurance.de</LinkInline>
              </p>
            </Prose>
          </section>

          {/* ─── 9. Aufsichtsbehörde ─────────────────────── */}
          <section id="aufsichtsbehoerde" className="mb-12 scroll-mt-24">
            <SectionTitle>9. Beschwerderecht bei der Aufsichtsbehörde</SectionTitle>
            <Prose>
              <p>
                Unbeschadet anderer Rechtsbehelfe haben Sie das Recht, sich gemäß Art. 77 DSGVO bei einer Datenschutz-Aufsichtsbehörde zu beschweren, wenn Sie der Ansicht sind, dass die Verarbeitung der Sie betreffenden personenbezogenen Daten gegen die DSGVO verstößt.
              </p>
              <p>Die für uns zuständige Aufsichtsbehörde ist:</p>
              <InfoBox>
                <strong className="text-white block mb-1">Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen (LDI NRW)</strong>
                Kavalleriestraße 2–4<br />
                40213 Düsseldorf<br /><br />
                Telefon: 0211 38424-0<br />
                E-Mail: <LinkInline href="mailto:poststelle@ldi.nrw.de">poststelle@ldi.nrw.de</LinkInline><br />
                Website: <LinkInline href="https://www.ldi.nrw.de" target="_blank" rel="noopener noreferrer">www.ldi.nrw.de</LinkInline>
              </InfoBox>
              <p>
                Sie können sich auch an jede andere Aufsichtsbehörde in dem EU-Mitgliedstaat Ihres gewöhnlichen Aufenthaltsorts oder Arbeitsplatzes wenden.
              </p>
            </Prose>
          </section>

          {/* ─── 10. Drittlandübermittlung ───────────────── */}
          <section id="drittlaender" className="mb-12 scroll-mt-24">
            <SectionTitle>10. Drittlandübermittlungen</SectionTitle>
            <Prose>
              <p>
                Im Rahmen des Betriebs dieser Website werden personenbezogene Daten in die USA übermittelt, da die eingesetzten Dienstleister (Cloudflare, Formspree) ihren Sitz in den USA haben. Die USA gelten nicht als Land mit einem der EU gleichwertigen Datenschutzniveau.
              </p>
              <p>
                Zum Schutz Ihrer Daten stützen wir uns auf folgende Garantien:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2 mt-3">
                <li>
                  <strong className="text-white">EU-U.S. Data Privacy Framework (DPF):</strong> Cloudflare ist als Unternehmen im DPF-Verzeichnis zertifiziert (ehemals Privacy Shield). Das DPF bietet ein von der EU-Kommission anerkanntes Schutzniveau für Übermittlungen in die USA.
                </li>
                <li>
                  <strong className="text-white">Standarddatenschutzklauseln (SCCs):</strong> Zusätzlich bzw. für Formspree werden von der EU-Kommission genehmigte Standardvertragsklauseln gemäß Art. 46 Abs. 2 lit. c DSGVO vereinbart.
                </li>
              </ul>
              <p className="mt-3">
                Weitere Informationen zu den abgeschlossenen Garantien erhalten Sie auf Anfrage unter <LinkInline href="mailto:team@kustura-sportscar-insurance.de">team@kustura-sportscar-insurance.de</LinkInline>.
              </p>
            </Prose>
          </section>

          {/* ─── 11. Aktualität ──────────────────────────── */}
          <section id="aktualitaet" className="mb-16 scroll-mt-24">
            <SectionTitle>11. Aktualität und Änderungen dieser Datenschutzerklärung</SectionTitle>
            <Prose>
              <p>
                Diese Datenschutzerklärung ist aktuell gültig und hat den Stand{' '}
                <strong className="text-white">
                  {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })}
                </strong>.
              </p>
              <p>
                Durch die Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher oder behördlicher Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern. Die jeweils aktuelle Version finden Sie stets auf dieser Seite.
              </p>
            </Prose>
          </section>

          {/* Back button */}
          <div className="border-t border-[#C9A961]/20 pt-10 flex items-center justify-between flex-wrap gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#C9A961] hover:text-white transition-colors duration-200 cursor-pointer group whitespace-nowrap"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-1 transition-transform duration-200"></i>
              <span style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: '16px', letterSpacing: '0.1em' }}>
                Zurück zur Startseite
              </span>
            </button>
            <p className="text-gray-600 text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
              © {new Date().getFullYear()} Kustura Sportscar Insurance · Vedran Kustura · Generalvertretung der Allianz
            </p>
          </div>
        </div>
      </main>
    </>
  );
}

/* ── Small helper components ─────────────────────────────── */

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-2xl font-bold text-white mb-5 pb-2 border-b border-[#C9A961]/25"
      style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.04em' }}
    >
      {children}
    </h2>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-gray-300 text-sm leading-relaxed space-y-3">
      {children}
    </div>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-zinc-950 border-l-2 border-[#C9A961] pl-4 pr-4 py-4 rounded-r-md text-sm leading-relaxed text-gray-300 my-4">
      {children}
    </div>
  );
}

function AddressBlock({ children }: { children: React.ReactNode }) {
  return (
    <address className="not-italic bg-zinc-950 border border-[#C9A961]/20 rounded-md px-5 py-4 text-sm leading-loose text-gray-300 mt-3 inline-block">
      {children}
    </address>
  );
}

function BoldLabel({ children }: { children: React.ReactNode }) {
  return <strong className="text-white">{children}</strong>;
}

function LinkInline({ href, children, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children: React.ReactNode }) {
  return (
    <a href={href} className="text-[#C9A961] hover:underline cursor-pointer" {...rest}>
      {children}
    </a>
  );
}

function RechtsRow({ art, children }: { art: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="shrink-0 bg-[#C9A961]/10 border border-[#C9A961]/30 text-[#C9A961] text-xs px-2 py-1 rounded font-mono mt-0.5 whitespace-nowrap">{art}</span>
      <p className="text-gray-300 text-sm leading-relaxed">{children}</p>
    </div>
  );
}

function RechtBlock({ art, children }: { art: string; children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-[#C9A961]/40 pl-4">
      <p className="text-[#C9A961] text-sm font-semibold mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>{art}</p>
      <p className="text-gray-300 text-sm leading-relaxed">{children}</p>
    </div>
  );
}

function DauerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-2">
      <span className="text-white text-sm font-semibold">{label}</span>
      <p className="text-gray-300 text-sm leading-relaxed">{children}</p>
    </div>
  );
}